import React, { FC, useRef, useEffect, useState, memo, useMemo } from 'react';
import * as eCharts from 'echarts';
import { lineTooltipFormatter } from '../Retention/linechart';
import { useIntl, getLocale } from 'umi';
import { ChartType } from '@intelligence/pages/TopCharts/_config/enums';
import usePieChartsHoverEffect from '@intelligence/components/Chart/hooks/usePieChartsHoverEffect';
import { getPieChartLabelBaseRich } from '@intelligence/utils/echartsUtils';
import { PIE_CHART_TOOLTIP_BASE_OPTION } from '@intelligence/utils/echartsConst';

interface RegionalPieChartProps {
  pieData: any[];
}

const color = [
  '#376AE7',
  '#3ECBFF',
  '#4FCB87',
  '#FFD14E',
  '#ED9142',
  '#E95F6A',
  '#CF61AC',
  '#8164CD',
  '#6585F7',
  '#A0B6FA',
  '#f19e55',
  '#69d19c',
];

const DevicePie: FC<RegionalPieChartProps> = (props) => {
  let myChart: any = null;
  const { pieData = [] } = props;
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const chartRef = useRef<any>(null);
  const [options, setOptions] = useState<any>();

  const pieDataList = useMemo(() => {
    return pieData?.map((item, index) => {
      return {
        ...item,
        name: locale === 'zh-CN' ? item?.name_zh : item?.name,
        seriesName: locale === 'zh-CN' ? item?.name_zh : item?.name,
        color: color[index],
      };
    });
  }, [pieData, locale]);

  const pieChartInstance = useRef<any>(null);
  const chartsInstance = useRef<any>({
    getPieInstance: () => {
      return pieChartInstance?.current;
    },
  });
  const [loading, setLoading] = useState(true);
  usePieChartsHoverEffect({
    loading,
    chartType: ChartType.Pie,
    chartsInstance,
    setOption: setOptions,
  });

  useEffect(() => {
    const gameLineOptions = {
      title: {
        text: formatMessage({ id: 'dbi_benchmark_system_device_up' }),
        left: 'center',
        top: '43%',
        textStyle: {
          fontWeight: 600,
          fontSize: 12,
          color: '#262832',
          width: 100,
          overflow: 'truncate',
        },
      },
      grid: {
        top: '2%',
        containLabel: true,
      },
      legend: {
        type: 'scroll',
        orient: 'horizontal',
        pageIconColor: '#ff781f',
        selectedMode: true,
        pageIconSize: 13,
        pageTextStyle: {
          color: '#999',
        },
        show: true,
        icon: 'circle',
        itemWidth: 20,
        itemHeight: 10,
        left: 'center',
        bottom: -4,
        textStyle: {
          color: '#888888',
          fontSize: 12,
        },
      },
      tooltip: {
        ...PIE_CHART_TOOLTIP_BASE_OPTION,
        show: true,
        confine: true,
        showDelay: 10,
        hideDelay: 100,
        extraCssText: 'border: none;',
        formatter: (param: any) => {
          return lineTooltipFormatter(
            pieDataList,
            formatMessage({ id: 'dbi_benchmark_system_device' }),
            '%',
            (value: any, data: any, target: any) => {
              return `${(target?.dod * 100)?.toFixed(2)}%` || '';
            },
          );
        },
      },
      series: [
        {
          data: pieDataList,
          type: 'pie',
          // radius: ['45px', '65px'],
          radius: [45, 65],
          avoidLabelOverlap: true,
          startAngle: 120,
          itemStyle: {
            borderRadius: 5,
            borderColor: '#fff',
            borderWidth: 1,
          },
          label: {
            normal: {
              padding: [0, -6],
              fontSize: 12,
              color: '#262832',
              opacity: 1,
              rich: {
                ...getPieChartLabelBaseRich(),
              },
              // formatter: formatPieChartLabel,
              formatter: (params) => {
                const name = params?.name;
                const percent =
                  params?.percent === undefined ? 0 : params?.percent;
                return `${name}:  {per|${
                  percent?.toFixed(2) || percent
                }%}\n{hr|}\n{a|}`;
              },
            },
          },
          labelLine: {
            // 折线弯曲程度配置
            length: 15,
            showAbove: false,
            // minTurnAngle: 80,
            maxSurfaceAngle: 80,
            lineStyle: {
              width: 2,
            },
          },
          name: 'Access From',
          minAngle: 5,
          bottom: 20,
        },
      ],
      graphic: [
        {
          type: 'text',
        },
      ],
    };

    setOptions(gameLineOptions);
  }, [pieData, locale]);

  const handleResize = () => {
    try {
      myChart.resize();
    } catch (err) {}
  };

  useEffect(() => {
    if (myChart === null) {
      myChart = eCharts.init(chartRef!.current!);
      pieChartInstance.current = myChart;
      setLoading(false);
    }
    options && myChart.setOption(options);
  }, [options]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [myChart]);

  return <div ref={chartRef} style={{ height: 214, width: '100%' }}></div>;
};

DevicePie.defaultProps = {
  pieData: [],
};

export default memo(DevicePie);
